import { cn } from '@/utils/cn';
import { cva, type VariantProps } from 'class-variance-authority';
import { CircleAlert, Info, TriangleAlert } from 'lucide-react';
import React from 'react';

const infoMessageVariants = cva('flex flex-col gap-4 rounded-lg p-4', {
  variants: {
    variant: {
      warning: 'bg-fill-info-tile-orange',
      alert: 'bg-fill-info-tile-red',
      info: 'bg-fill-info-tile-blue',
    },
  },
});

const titlePillVariants = cva('text-body-m-semibold inline-flex w-fit items-center gap-2 rounded-full px-3 py-1.5', {
  variants: {
    variant: {
      warning: 'bg-fill-badge-orange text-text-default-primary',
      alert: 'bg-fill-badge-brand text-text-default-white',
      info: 'bg-fill-badge-blue text-text-default-white',
    },
  },
});

const icons: Record<InfoMessageVariant, React.ReactNode> = {
  warning: <CircleAlert className='size-5' />,
  alert: <TriangleAlert className='size-5' />,
  info: <Info className='size-5' />,
};

type InfoMessageVariant = 'warning' | 'alert' | 'info';

interface InfoMessageProps extends VariantProps<typeof infoMessageVariants> {
  variant: InfoMessageVariant;
  title?: string;
  description?: string;
  className?: string;
  children?: React.ReactNode;
}

export const InfoMessage = ({ variant, title, description, className, children }: InfoMessageProps) => {
  return (
    <div className={cn(infoMessageVariants({ variant }), className)}>
      <div className={cn(titlePillVariants({ variant }))}>
        {icons[variant]}
        <span>{title}</span>
      </div>
      {description && <p className='text-body-m-regular'>{description}</p>}
      {children}
    </div>
  );
};
